import { lazy, Suspense } from 'react';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { PermissionProvider } from './context/PermissionContext';
import { NotificationProvider } from './context/NotificationContext';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/layout/AppLayout';
import LoginPage from './pages/LoginPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import theme from './theme';

// Lazy load pages for better performance
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const RoleManagementPage = lazy(() => import('./pages/RoleManagementPage'));
const AttendancePage = lazy(() => import('./pages/AttendancePage'));
const AttendanceApprovalsPage = lazy(() => import('./pages/AttendanceApprovalsPage'));
const LeavePage = lazy(() => import('./pages/LeavePage'));
const LeaveApprovalsPage = lazy(() => import('./pages/LeaveApprovalsPage'));
const OvertimePage = lazy(() => import('./pages/OvertimePage'));
const OvertimeApprovalsPage = lazy(() => import('./pages/OvertimeApprovalsPage'));
const ServiceRecordingPage = lazy(() => import('./pages/ServiceRecordingPage'));
const ServiceTemplateManagementPage = lazy(() => import('./pages/ServiceTemplateManagementPage'));
const ClientManagementPage = lazy(() => import('./pages/ClientManagementPage'));
const PayslipsPage = lazy(() => import('./pages/PayslipsPage'));
const PayrollManagementPage = lazy(() => import('./pages/PayrollManagementPage'));
const AnnouncementManagementPage = lazy(() => import('./pages/AnnouncementManagementPage'));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Loading fallback component
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
);

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <NotificationProvider>
            <BrowserRouter>
              <AuthProvider>
                <PermissionProvider>
                  <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/unauthorized" element={<UnauthorizedPage />} />
                  
                  {/* Protected routes with layout */}
                  <Route
                    element={
                      <ProtectedRoute>
                        <AppLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route path="/" element={<DashboardPage />} />
                    
                    {/* Attendance routes */}
                    <Route
                      path="/attendance"
                      element={
                        <ProtectedRoute requiredPermission="attendance:view">
                          <AttendancePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/attendance/approvals"
                      element={
                        <ProtectedRoute requiredPermission="attendance:approve">
                          <AttendanceApprovalsPage />
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* Leave routes */}
                    <Route
                      path="/leave"
                      element={
                        <ProtectedRoute requiredPermission="leave:view">
                          <LeavePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/leave/approvals"
                      element={
                        <ProtectedRoute requiredPermission="leave:approve">
                          <LeaveApprovalsPage />
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* Overtime routes */}
                    <Route
                      path="/overtime"
                      element={
                        <ProtectedRoute requiredPermission="overtime:view">
                          <OvertimePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/overtime/approvals"
                      element={
                        <ProtectedRoute requiredPermission="overtime:approve">
                          <OvertimeApprovalsPage />
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* Service recording routes */}
                    <Route
                      path="/service/records"
                      element={
                        <ProtectedRoute requiredPermission="service:view">
                          <ServiceRecordingPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/service/templates"
                      element={
                        <ProtectedRoute requiredPermission="service:manage_templates">
                          <ServiceTemplateManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* Payroll routes */}
                    <Route
                      path="/payslips"
                      element={
                        <ProtectedRoute requiredPermission="payroll:view_own">
                          <PayslipsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/payroll/manage"
                      element={
                        <ProtectedRoute requiredPermission="payroll:compute">
                          <PayrollManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* Admin routes */}
                    <Route
                      path="/admin/users"
                      element={
                        <ProtectedRoute requiredPermission="admin:manage_users">
                          <UserManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/roles"
                      element={
                        <ProtectedRoute requiredPermission="admin:manage_roles">
                          <RoleManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/clients"
                      element={
                        <ProtectedRoute requiredPermission="admin:manage_clients">
                          <ClientManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/announcements"
                      element={
                        <ProtectedRoute requiredPermission="admin:manage_announcements">
                          <AnnouncementManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/audit-logs"
                      element={
                        <ProtectedRoute requiredPermission="audit:view">
                          <AuditLogPage />
                        </ProtectedRoute>
                      }
                    />
                  </Route>
                  
                  {/* Catch all */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </PermissionProvider>
          </AuthProvider>
        </BrowserRouter>
          </NotificationProvider>
      </ThemeProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
